import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, type Integration } from '../api/client.ts';
import { formatLastSeen } from '../lib/format.ts';

/**
 * The «Интеграции» tab: outside services allowed to post into this chat.
 *
 * Its own file, and its own fetch, rather than another section on the admin
 * screen. That page polls every three seconds to keep its counters live, and a
 * list of integrations does not change under anybody — so this loads when the
 * tab is opened and after each change, and nothing else.
 *
 * A local Pill rather than the one in AdminScreen.tsx: importing it back out of
 * that file would make the two import each other, and the admin screen is due
 * to be split into per-tab files anyway. When that happens both go to one place.
 */
function Pill({ tone, children }: { tone: 'good' | 'neutral'; children: React.ReactNode }) {
  return <span className={`pill pill-${tone}`}>{children}</span>;
}

const EMPTY_DRAFT = { name: '', slug: '', serviceRead: false };

export function AdminIntegrations() {
  const [rows, setRows] = useState<Integration[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  /**
   * The token that has just been minted, held on screen until it is dismissed.
   *
   * It exists nowhere else — the server keeps a digest — so the list is not
   * refreshed under it and nothing about this component may re-render it away
   * on a timer. Losing it means rotating and copying again, which is survivable
   * but pointless.
   */
  const [fresh, setFresh] = useState<{ name: string; token: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const tokenBlock = useRef<HTMLPreElement>(null);

  const load = useCallback(async () => {
    try {
      setRows(await api.listIntegrations());
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить интеграции');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** Every change goes through here: one place to be busy, notice and reload in. */
  async function change(what: string, run: () => Promise<void>) {
    setBusy(true);
    setNotice(null);
    try {
      await run();
      await load();
      setNotice(what);
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : 'Не получилось');
    }
    setBusy(false);
  }

  async function create(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setNotice(null);
    try {
      const made = await api.createIntegration(draft);
      setFresh({ name: made.integration.name, token: made.token });
      setCopied(false);
      setDraft(EMPTY_DRAFT);
      await load();
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : 'Не удалось создать интеграцию');
    }
    setBusy(false);
  }

  async function rotate(row: Integration) {
    if (!confirm(`Выдать новый токен для «${row.name}»? Старый перестанет работать сразу.`)) return;
    setBusy(true);
    setNotice(null);
    try {
      const { token } = await api.rotateIntegrationToken(row.id);
      setFresh({ name: row.name, token });
      setCopied(false);
      await load();
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : 'Не удалось выдать токен');
    }
    setBusy(false);
  }

  async function copyToken() {
    if (!fresh) return;
    try {
      await navigator.clipboard.writeText(fresh.token);
      setCopied(true);
      return;
    } catch {
      // Fall through: the WebView in the phone shell fences the clipboard API
      // off, and that is exactly where a token is hardest to copy by hand.
    }
    const node = tokenBlock.current;
    if (!node) return;
    const range = document.createRange();
    range.selectNodeContents(node);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
    // The deprecated path still works from a user gesture where the modern one
    // is refused — and this handler is one. Selection first, so its failure
    // leaves the reader one long-press away rather than one apology away.
    if (document.execCommand('copy')) {
      selection?.removeAllRanges();
      setCopied(true);
    } else {
      setNotice('Буфер обмена недоступен — токен выделен, скопируйте вручную');
    }
  }

  if (error && !rows) return <div className="admin-error">{error}</div>;
  if (!rows) return <div className="admin-section" />;

  return (
    <>
      {fresh && (
        <section className="admin-section">
          <h2 className="admin-section-title">Токен для «{fresh.name}»</h2>
          <p className="admin-note">
            Показывается один раз — на сервере хранится только его отпечаток. Скопируйте сейчас и
            положите в <code>CHAT_INTEGRATION_TOKEN</code> на стороне сервиса. Если потеряется —
            выдайте новый, старый при этом перестанет работать. Если интеграции даны сервисные
            отчёты, этот же токен читает <code>/api/service</code> — журнал звонков и ошибки
            клиентов.
          </p>
          <pre className="admin-env" ref={tokenBlock}>
            {fresh.token}
          </pre>
          <div className="admin-actions">
            <button type="button" className="primary-button admin-save" onClick={() => void copyToken()}>
              {copied ? 'Скопировано' : 'Скопировать'}
            </button>
            <button
              type="button"
              className="settings-secondary"
              onClick={() => setFresh(null)}
            >
              Я скопировал
            </button>
          </div>
        </section>
      )}

      <section className="admin-section">
        <h2 className="admin-section-title">Интеграции</h2>

        <form className="admin-tuning" onSubmit={(event) => void create(event)}>
          <div className="admin-tuning-head">
            <h3 className="admin-tuning-title">Новая интеграция</h3>
            <span className="admin-tuning-hint">
              Заявки с сайта и всё остальное, что должно писать сюда само. Для интеграции заводится
              отдельный аккаунт-бот: войти под ним нельзя. Новый бот виден только админу — кому его
              открыть, решается на вкладке «Боты».
            </span>
          </div>

          <div className="admin-fields">
            <label className="admin-field">
              <span>Название</span>
              <input
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                placeholder="Сайт Ольги"
                maxLength={64}
              />
              <small>его видно в чате как имя отправителя</small>
            </label>
            <label className="admin-field">
              <span>Идентификатор</span>
              <input
                value={draft.slug}
                onChange={(event) => setDraft({ ...draft, slug: event.target.value })}
                placeholder="site"
                maxLength={25}
                spellCheck={false}
              />
              <small>латиница, цифры и дефис — станет никнеймом бота</small>
            </label>
            <label className="admin-field">
              <span>Сервисные отчёты</span>
              <input
                type="checkbox"
                checked={draft.serviceRead}
                onChange={(event) => setDraft({ ...draft, serviceRead: event.target.checked })}
              />
              <small>токен также читает журнал звонков и ошибки клиентов</small>
            </label>
          </div>

          <div className="admin-actions">
            <button
              type="submit"
              className="primary-button admin-save"
              disabled={busy || !draft.name.trim() || !draft.slug.trim()}
            >
              Создать
            </button>
            {notice && <span className="admin-notice">{notice}</span>}
          </div>
        </form>

        {rows.length === 0 ? (
          <p className="admin-note">Пока ни одной. Первая — заявки с сайта.</p>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Название</th>
                  <th>Бот</th>
                  <th>Кому приходит</th>
                  <th>Токен</th>
                  <th>Заявок</th>
                  <th>Последняя</th>
                  <th>Состояние</th>
                  <th>Отчёты</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.name}</td>
                    <td className="admin-hosts">{row.botUsername}</td>
                    {/* A list and not a picker: nobody chooses this here. It is
                        where the bot actually delivers, and it is on the page
                        because «дошло ли до Оли» is exactly what somebody
                        looking at this table wants to know.

                        Not the same list as «Боты» shows, and that is why it
                        stayed: this one includes every group the bot was put
                        into, which access has nothing to do with. */}
                    <td>
                      {row.targets.length === 0 ? (
                        <span className="admin-hosts">никто не добавил</span>
                      ) : (
                        row.targets.map((target) => target.title).join(', ')
                      )}
                    </td>
                    <td className="admin-hosts">{row.tokenPrefix}…</td>
                    <td>{row.messages}</td>
                    <td className="admin-hosts">
                      {row.lastUsedAt === null ? 'никогда' : formatLastSeen(row.lastUsedAt)}
                    </td>
                    <td>
                      {row.disabled ? (
                        <Pill tone="neutral">выключена</Pill>
                      ) : (
                        <Pill tone="good">включена</Pill>
                      )}
                    </td>
                    <td>
                      {row.serviceRead ? (
                        <Pill tone="good">даны</Pill>
                      ) : (
                        <Pill tone="neutral">нет</Pill>
                      )}
                    </td>
                    <td className="admin-row-actions">
                      <button
                        type="button"
                        className="settings-secondary"
                        disabled={busy}
                        onClick={() =>
                          void change(row.disabled ? 'Включена' : 'Выключена', () =>
                            api
                              .updateIntegration(row.id, { disabled: !row.disabled })
                              .then(() => undefined),
                          )
                        }
                      >
                        {row.disabled ? 'Включить' : 'Выключить'}
                      </button>
                      <button
                        type="button"
                        className="settings-secondary"
                        disabled={busy}
                        onClick={() =>
                          void change(row.serviceRead ? 'Отчёты забраны' : 'Отчёты даны', () =>
                            api
                              .updateIntegration(row.id, { serviceRead: !row.serviceRead })
                              .then(() => undefined),
                          )
                        }
                      >
                        {row.serviceRead ? 'Забрать отчёты' : 'Дать отчёты'}
                      </button>
                      <button
                        type="button"
                        className="settings-secondary"
                        disabled={busy}
                        onClick={() => void rotate(row)}
                      >
                        Новый токен
                      </button>
                      <button
                        type="button"
                        className="settings-secondary"
                        disabled={busy}
                        onClick={() => {
                          if (
                            !confirm(
                              `Удалить «${row.name}»? Аккаунт бота и всё, что он уже прислал, останутся — перестанет работать только токен.`,
                            )
                          ) {
                            return;
                          }
                          void change('Удалена', () => api.deleteIntegration(row.id));
                        }}
                      >
                        Удалить
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="admin-note">
          Заявки приходят во все чаты, где есть бот. Личный чат заводится, когда боту дают доступ на
          вкладке «Боты», — оттуда же его и забирают, и тогда чат уезжает из списка, а переписка
          остаётся. В общую группу бота добавляют через «Новая группа», и на это доступ не влияет:
          кто в группе, тот заявки и видит. Если бота нет нигде, сервис на той стороне получит
          ошибку и подождёт с очередью.
        </p>
      </section>
    </>
  );
}
